import { createContext, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { BACKEND_URL, formatErr } from "../../lib/api";
import { useCustomerAuth } from "../../lib/customerAuth";
import { useAuth } from "../../lib/auth";
import { getLocale } from "../../lib/i18n";
import { track } from "../../lib/analytics";
import { toast } from "sonner";

/**
 * Sprint H · Batch 2 — Context de la reserva pública.
 *
 * Concentra TODO el estado del funnel para evitar prop-drilling:
 *  - data (organización + catálogo) y cfg (Turnstile)
 *  - service / operator / addonQty / date / slot
 *  - form (customer / vehicle / pickup)
 *  - locale, step, turnstileToken
 *  - submit() y resetAll()
 *
 * Persistencia: hidrata/escribe en localStorage el WIP por `slug`.
 * Customer logueado: autorrellena los campos.
 */
const pub = axios.create({ baseURL: `${BACKEND_URL}/api`, withCredentials: true });
const STORAGE_KEY = (slug) => `autoglow_booking_${slug}`;
const STORAGE_VERSION = 1;

export const STEP_OPERATOR = 1;
export const STEP_SERVICE = 2;
export const STEP_ADDONS = 3;
export const STEP_SLOT = 4;
export const STEP_DATA = 5;
export const STEP_DONE = 6;

const BookingContext = createContext(null);

export function BookingProvider({ slug, children }) {
  const [locale, setLocaleState] = useState(getLocale());
  const [data, setData] = useState(null);
  const [operators, setOperators] = useState([]);
  const [cfg, setCfg] = useState({ turnstile_site_key: "", turnstile_enabled: false });
  const [loadingOrg, setLoadingOrg] = useState(true);
  const [step, setStep] = useState(STEP_SERVICE);
  const [service, setService] = useState(null);
  const [operator, setOperator] = useState(null);
  const [addonQty, setAddonQty] = useState({});
  const [date, setDate] = useState(new Date(Date.now() + 24 * 3600 * 1000));
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({
    license_plate: "", customer_name: "", customer_email: "",
    customer_phone: "", car_make_model: "", notes: "",
    customer_type: "particular",
    razon_social: "", nif_cif: "", address_fiscal: "",
    pickup_service: false, pickup_address: "", pickup_time_preference: "",
  });
  const [turnstileToken, setTurnstileToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [booked, setBooked] = useState(null);
  const { account: customerAccount } = useCustomerAuth() || {};
  const { user: orgUser } = useAuth() || {};
  const hydrated = useRef(false);

  // Load org/cfg/operators
  useEffect(() => {
    (async () => {
      try {
        const [o, c, ops] = await Promise.all([
          pub.get(`/public/org/${slug}`),
          pub.get(`/public/config`),
          pub.get(`/public/org/${slug}/operators`),
        ]);
        setData(o.data);
        setCfg(c.data);
        setOperators(ops.data.operators || []);
        // ANA-1 · org_page_view (al cargar ficha pública)
        track("org_page_view", { orgSlug: slug, orgId: o.data?.org?.id });
        const stored = window.localStorage.getItem("ww_locale");
        if (!stored && o.data?.org?.default_language) setLocaleState(o.data.org.default_language);
      } catch {
        toast.error("Centro no encontrado");
      } finally { setLoadingOrg(false); }
    })();
  }, [slug]);

  // Customer auth → autorrellena
  useEffect(() => {
    if (!customerAccount) return;
    setForm((f) => ({
      ...f,
      customer_name: f.customer_name || customerAccount.name || "",
      customer_email: f.customer_email || customerAccount.email || "",
      customer_phone: f.customer_phone || customerAccount.phone || "",
      pickup_address: f.pickup_address || customerAccount.default_address || "",
      license_plate: f.license_plate || (customerAccount.license_plates?.[0] || ""),
    }));
  }, [customerAccount]);

  // Sprint B · #2 — B2B: si viene ?as_org=1 y hay user logueado del SaaS,
  // pre-rellena con los datos fiscales de la organización del usuario.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("as_org") !== "1") return;
    if (!orgUser?.organization_id) return;
    (async () => {
      try {
        const { api } = await import("../../lib/api");
        const me = await api.get("/auth/me");
        const org = me.data?.organization;
        if (!org) return;
        setForm((f) => ({
          ...f,
          customer_type: "empresa",
          customer_name: f.customer_name || org.name || "",
          customer_email: f.customer_email || org.notification_email || org.contact_email || orgUser.email || "",
          customer_phone: f.customer_phone || org.phone || "",
          razon_social: f.razon_social || org.name || "",
          nif_cif: f.nif_cif || org.nif || "",
          address_fiscal: f.address_fiscal || [org.address, org.city].filter(Boolean).join(", "),
          notes: f.notes || `Reserva B2B realizada por ${org.name} (cliente AutoGlow Ecosystem).`,
        }));
        toast.success(`Reservando como ${org.name}`);
      } catch {
        // silent
      }
    })();
  }, [orgUser]);

  // Hydrate WIP
  useEffect(() => {
    if (!data || hydrated.current) return;
    hydrated.current = true;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY(slug));
      if (!raw) return;
      const wip = JSON.parse(raw);
      if (wip.v !== STORAGE_VERSION) return;
      if (wip.serviceId && data.catalog?.length) {
        const s = data.catalog.find((c) => c.id === wip.serviceId);
        if (s) setService(s);
      }
      if (wip.operatorId && operators.length) {
        const op = operators.find((o) => o.id === wip.operatorId);
        if (op) setOperator(op);
      }
      if (wip.addonQty) setAddonQty(wip.addonQty);
      if (wip.dateISO) setDate(new Date(wip.dateISO));
      if (wip.form) setForm((f) => ({ ...f, ...wip.form }));
      if (typeof wip.step === "number" && wip.step >= 1 && wip.step < STEP_DONE) setStep(wip.step);
    } catch {}
  }, [data, operators, slug]);

  // Persist WIP
  useEffect(() => {
    if (!data) return;
    const wip = {
      v: STORAGE_VERSION,
      step, serviceId: service?.id || null, operatorId: operator?.id || null,
      addonQty, dateISO: date?.toISOString(), form,
    };
    try { window.localStorage.setItem(STORAGE_KEY(slug), JSON.stringify(wip)); } catch {}
  }, [step, service, operator, addonQty, date, form, slug, data]);

  // Slot fetch
  useEffect(() => {
    if (!service || step !== STEP_SLOT) return;
    const dStr = date.toISOString().slice(0, 10);
    pub.get(`/public/org/${slug}/slots?date=${dStr}&duration=${service.duration_min || 60}`)
      .then((r) => setSlots(r.data.slots || []));
  }, [service, date, slug, step]);

  const addonItems = service?.addons || [];
  const addonsSubtotal = addonItems.reduce((acc, a) => acc + ((addonQty[a.id] || 0) * (a.price_per_unit || 0)), 0);
  const total = (service?.price || 0) + addonsSubtotal;

  const submit = async () => {
    setBusy(true);
    try {
      const addonsSelections = Object.entries(addonQty)
        .filter(([, q]) => q > 0)
        .map(([id, q]) => ({ id, qty: q }));
      const { data: bdata } = await pub.post(`/public/org/${slug}/book`, {
        date: slot.iso,
        license_plate: form.license_plate,
        customer_name: form.customer_name,
        customer_email: form.customer_email || null,
        customer_phone: form.customer_phone,
        car_make_model: form.car_make_model,
        wash_type: service.name,
        operario_id: null,
        notes: form.notes,
        customer_type: form.customer_type,
        razon_social: form.razon_social,
        nif_cif: form.nif_cif,
        address_fiscal: form.address_fiscal,
        addons: addonsSelections,
        language: locale,
        turnstile_token: turnstileToken,
        pickup_service: !!form.pickup_service,
        pickup_address: form.pickup_address,
        pickup_time_preference: form.pickup_time_preference,
        customer_account_id: customerAccount?.id || null,
      });
      setBooked(bdata); setStep(STEP_DONE);
      // ANA-1 · booking_submit (reserva enviada con éxito)
      track("booking_submit", { orgSlug: slug, orgId: data?.org?.id, metadata: { service: service?.name, total } });
      try { window.localStorage.removeItem(STORAGE_KEY(slug)); } catch {}
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const resetAll = () => {
    setStep(STEP_SERVICE); setService(null); setOperator(null); setAddonQty({}); setSlot(null);
    setForm({
      license_plate: "", customer_name: "", customer_email: "", customer_phone: "",
      car_make_model: "", notes: "", customer_type: "particular", razon_social: "", nif_cif: "", address_fiscal: "",
      pickup_service: false, pickup_address: "", pickup_time_preference: "",
    });
    try { window.localStorage.removeItem(STORAGE_KEY(slug)); } catch {}
  };

  const value = {
    slug, locale, setLocale: setLocaleState,
    data, cfg, loadingOrg, customerAccount,
    operators, operator, setOperator,
    step, setStep,
    service, setService,
    addonQty, setAddonQty, addonItems, addonsSubtotal,
    date, setDate, slots, slot, setSlot,
    form, setForm,
    turnstileToken, setTurnstileToken,
    busy, booked,
    total,
    submit, resetAll,
  };

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
}

export function useBooking() {
  const ctx = useContext(BookingContext);
  if (!ctx) throw new Error("useBooking() must be used within <BookingProvider>");
  return ctx;
}
